/**
 * ShopLC common request headers (shared helper).
 *
 * Copy this same file into any theme app extension that calls our backend APIs,
 * load it before the extension's own scripts, then call `shoplcFetch(url, options)`
 * instead of `fetch(url, options)`. It works like `fetch` and adds:
 *
 *   x-session-id       LogRocket session ID (read once per page load). Falls back to
 *                      this request's x-request-id when LogRocket is not ready.
 *   x-channel          Always "web" (storefront only; the mobile app calls the backend directly).
 *   x-request-id       New for every call: UUID + "-" + Unix timestamp in seconds.
 *   x-shoplc-trace-id  "customerId:cfRay" for logged-in customers, "cfRay" for guests.
 *                      The cf-ray is the Cloudflare ray ID of the first same-origin response
 *                      on this page load (kept in memory only; no generated ID, no localStorage).
 *                      Header is left out when neither value is available.
 *
 * Headers already set by the caller are never overwritten. If anything fails while
 * building the headers, the request still goes out as a plain fetch.
 *
 * Also exposed: `window.ShopLCRequestHeaders.get()` returns a Promise of the header
 * object, for code that does not use fetch (e.g. XMLHttpRequest).
 */
(function () {
  "use strict";

  var VERSION = "1.1.0";

  // Several extensions can load this file on the same page; the first copy wins so
  // every extension shares the same session and device IDs.
  if (window.ShopLCRequestHeaders) return;

  var HEADER_SESSION = "x-session-id";
  var HEADER_CHANNEL = "x-channel";
  var HEADER_REQUEST = "x-request-id";
  var HEADER_TRACE = "x-shoplc-trace-id";
  var CHANNEL_VALUE = "web";

  var LOGROCKET_TIMEOUT_MS = 1000;
  var CF_RAY_TIMEOUT_MS = 1000;

  var nativeFetch = window.fetch;

  // ---------- x-request-id ----------

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    var bytes = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === "function") {
      window.crypto.getRandomValues(bytes);
    } else {
      for (var i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = [];
    for (var j = 0; j < 16; j++) hex.push((bytes[j] + 0x100).toString(16).slice(1));
    return (
      hex.slice(0, 4).join("") + "-" + hex.slice(4, 6).join("") + "-" +
      hex.slice(6, 8).join("") + "-" + hex.slice(8, 10).join("") + "-" +
      hex.slice(10, 16).join("")
    );
  }

  function createRequestId() {
    return uuid() + "-" + Math.floor(Date.now() / 1000);
  }

  // ---------- x-session-id (LogRocket) ----------

  // Kept in memory only, so a page refresh reads it again.
  var lrSessionId = "";
  var lrPending = null;

  function parseLogRocketSessionId(sessionUrl) {
    if (typeof sessionUrl !== "string") return "";
    var afterS = sessionUrl.split("/s/")[1];
    return afterS ? afterS.split("/")[0] || "" : "";
  }

  function getLogRocketSessionId() {
    if (lrSessionId) return Promise.resolve(lrSessionId);
    var lr = window.LogRocket;
    if (!lr || typeof lr.getSessionURL !== "function") return Promise.resolve("");
    if (lrPending) return lrPending;

    lrPending = new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        lrPending = null;
        resolve("");
      }, LOGROCKET_TIMEOUT_MS);

      try {
        lr.getSessionURL(function (sessionUrl) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          lrSessionId = parseLogRocketSessionId(sessionUrl);
          lrPending = null;
          resolve(lrSessionId);
        });
      } catch (e) {
        settled = true;
        clearTimeout(timer);
        lrPending = null;
        resolve("");
      }
    });
    return lrPending;
  }

  // ---------- x-shoplc-trace-id ----------

  // The cf-ray is the Cloudflare ray ID of a same-origin response. Nothing is generated
  // or stored: it is kept in memory only, the first one seen on this page load.
  var cfRay = "";
  var cfRayPending = null;

  function rememberCfRay(response) {
    try {
      if (cfRay) return;
      var ray = response && response.headers && response.headers.get("cf-ray");
      if (ray) cfRay = ray;
    } catch (e) {}
  }

  // Returns the page's cf-ray. If no same-origin response was seen yet, one tiny HEAD
  // request to the store's own /robots.txt is made (max CF_RAY_TIMEOUT_MS) to read it.
  function getCfRay() {
    if (cfRay) return Promise.resolve(cfRay);
    if (cfRayPending) return cfRayPending;

    cfRayPending = new Promise(function (resolve) {
      var timer = setTimeout(function () {
        cfRayPending = null;
        resolve("");
      }, CF_RAY_TIMEOUT_MS);

      try {
        nativeFetch
          .call(window, window.location.origin + "/robots.txt", { method: "HEAD" })
          .then(function (response) {
            clearTimeout(timer);
            rememberCfRay(response);
            cfRayPending = null;
            resolve(cfRay);
          })
          .catch(function () {
            clearTimeout(timer);
            cfRayPending = null;
            resolve("");
          });
      } catch (e) {
        clearTimeout(timer);
        cfRayPending = null;
        resolve("");
      }
    });
    return cfRayPending;
  }

  function getCustomerId() {
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.page && meta.page.customerId) return String(meta.page.customerId);
      if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
        return String(window.Shopify.customer.id);
      }
      var el = document.querySelector("[data-customer-id]");
      var fromDom = el && el.getAttribute("data-customer-id");
      if (fromDom && fromDom.trim()) return fromDom.trim();
    } catch (e) {}
    return "";
  }

  // "customerId:cfRay" when logged in, "cfRay" for guests. Empty when neither is known.
  function getTraceId() {
    return getCfRay().then(function (ray) {
      var customerId = getCustomerId();
      if (customerId && ray) return customerId + ":" + ray;
      return customerId || ray || "";
    });
  }

  // ---------- public API ----------

  function getHeaders() {
    var requestId = createRequestId();
    return Promise.all([getLogRocketSessionId(), getTraceId()]).then(function (values) {
      var headers = {};
      headers[HEADER_SESSION] = values[0] || requestId;
      headers[HEADER_CHANNEL] = CHANNEL_VALUE;
      headers[HEADER_REQUEST] = requestId;
      if (values[1]) headers[HEADER_TRACE] = values[1];
      return headers;
    });
  }

  function isSameOrigin(url) {
    try {
      return new URL(url, window.location.href).origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  function shoplcFetch(input, init) {
    var url = typeof input === "string" ? input : (input && input.url) || String(input);

    return getHeaders()
      .then(function (extra) {
        var isRequest = typeof Request !== "undefined" && input instanceof Request;
        var headers = new Headers((init && init.headers) || (isRequest ? input.headers : undefined));
        Object.keys(extra).forEach(function (name) {
          if (!headers.has(name)) headers.set(name, extra[name]);
        });
        var options = Object.assign({}, init, { headers: headers });
        return isRequest ? new Request(input, options) : { url: input, options: options };
      })
      .catch(function () {
        return null; // header problem: send the call without the extra headers
      })
      .then(function (prepared) {
        var call;
        if (!prepared) call = nativeFetch.call(window, input, init);
        else if (prepared instanceof Request) call = nativeFetch.call(window, prepared);
        else call = nativeFetch.call(window, prepared.url, prepared.options);

        return call.then(function (response) {
          if (isSameOrigin(url)) rememberCfRay(response);
          return response;
        });
      });
  }

  window.shoplcFetch = shoplcFetch;
  window.ShopLCRequestHeaders = {
    version: VERSION,
    get: getHeaders,
    fetch: shoplcFetch,
  };
})();
